import type { FundGridQuery, FundSummary } from '@nemo/api';
import {
  isFundSortColumn,
  removeStageChip,
  stageFilterChipStages,
  stageFilterWants,
  type ListStatus,
  type Stage,
} from '@nemo/core/model';
import { ALL_INVESTOR_TYPES, countryName, investorTypeLabel } from '../dealroom/format.js';
import { stageFilterLabel, type FilterChip, type StageMode } from '../ui/FilterDialog.js';
import type { Sort } from './columns.js';

/** The Region filter's shorthand for the six European regions, never a region itself. */
export const EUROPE_FILTER = 'Europe';

export type CoInvestorFilter = '' | 'only' | 'never';
/** How recent the team's last contact with the fund is. */
export type ContactRecencyFilter = '' | FundSummary['lastContact']['recency'];
/** Whether the only people who know the fund well have left Project A. */
export type ReachFilter = '' | 'onlyFormerTeam' | 'notOnlyFormerTeam';
/** How busy the fund has been: its rounds in the last 24 months. */
export type ActivityFilter = '' | 'active' | 'busy' | 'quiet';

/**
 * Every narrowing the funds grid offers apart from the search box, in one
 * value: the dialog edits it, the summary row reads it back as chips, and the
 * server filters by it. Within one filter any value matches; filters AND.
 */
export interface FundGridFilters {
  readonly knownBy: readonly string[];
  readonly coInvestor: CoInvestorFilter;
  readonly stageMode: StageMode;
  readonly stages: readonly Stage[];
  readonly types: readonly string[];
  readonly sectors: readonly string[];
  readonly regions: readonly string[];
  readonly tier: string;
  readonly hq: readonly string[];
  readonly activity: ActivityFilter;
  readonly contact: ContactRecencyFilter;
  readonly reach: ReachFilter;
}

export const NO_FILTERS: FundGridFilters = {
  knownBy: [],
  coInvestor: '',
  stageMode: 'invests',
  stages: [],
  types: [],
  sectors: [],
  regions: [],
  tier: '',
  hq: [],
  activity: '',
  contact: '',
  reach: '',
};

export const CONTACT_LABELS: Record<Exclude<ContactRecencyFilter, ''>, string> = {
  recent: 'Contact in last 2 years',
  lapsed: 'Lapsed (older contact)',
  never: 'Never',
};

export const REACH_LABELS: Record<Exclude<ReachFilter, ''>, string> = {
  onlyFormerTeam: 'Only former or support staff know them',
  notOnlyFormerTeam: 'Not only former or support staff',
};

export const ACTIVITY_LABELS: Record<Exclude<ActivityFilter, ''>, string> = {
  active: 'Any round in 24 months',
  busy: '5+ rounds in 24 months',
  quiet: 'No rounds in 24 months',
};

export const CO_INVESTOR_LABELS: Record<Exclude<CoInvestorFilter, ''>, string> = {
  only: 'Co-investors only',
  never: 'Never co-invested',
};

/** What the server is asked: these filters and this sort over one half of the list. */
export function gridQueryOf(
  listStatus: ListStatus,
  search: string,
  filters: FundGridFilters,
  sort: Sort | null,
): FundGridQuery {
  const stages = stageFilterWants({ mode: filters.stageMode, stages: filters.stages });
  const types = ALL_INVESTOR_TYPES.filter((type) => filters.types.includes(type));
  return {
    listStatus,
    ...(search === '' ? {} : { search }),
    ...(filters.knownBy.length === 0 ? {} : { knownBy: [...filters.knownBy] }),
    ...(filters.coInvestor === '' ? {} : { coInvestor: filters.coInvestor }),
    ...(stages === null
      ? {}
      : stages.kind === 'leads'
        ? { leadsAt: [...stages.stages] }
        : { participatesAt: [...stages.stages] }),
    ...(types.length === 0 ? {} : { types }),
    ...(filters.sectors.length === 0 ? {} : { sectors: [...filters.sectors] }),
    ...(filters.regions.length === 0 ? {} : { regions: [...filters.regions] }),
    ...(filters.tier === '' ? {} : { tier: Number(filters.tier) }),
    ...(filters.hq.length === 0 ? {} : { hq: [...filters.hq] }),
    ...(filters.activity === '' ? {} : { activity: filters.activity }),
    ...(filters.contact === '' ? {} : { contact: filters.contact }),
    ...(filters.reach === '' ? {} : { onlyFormerTeam: filters.reach === 'onlyFormerTeam' }),
    ...(sort !== null && isFundSortColumn(sort.id)
      ? { sort: { column: sort.id, direction: sort.direction } }
      : {}),
  };
}

export function without<T>(values: readonly T[], value: T): T[] {
  return values.filter((candidate) => candidate !== value);
}

/** Every active value, in the dialog's order, each removable on its own. */
export function filterChips(filters: FundGridFilters): FilterChip<FundGridFilters>[] {
  const chips: FilterChip<FundGridFilters>[] = [];
  for (const name of filters.knownBy) {
    chips.push({
      key: `knownBy:${name}`,
      label: `Known by ${name}`,
      remove: (f) => ({ ...f, knownBy: without(f.knownBy, name) }),
    });
  }
  if (filters.contact !== '') {
    chips.push({
      key: 'contact',
      label: CONTACT_LABELS[filters.contact],
      remove: (f) => ({ ...f, contact: '' }),
    });
  }
  if (filters.reach !== '') {
    chips.push({
      key: 'reach',
      label: REACH_LABELS[filters.reach],
      remove: (f) => ({ ...f, reach: '' }),
    });
  }
  if (filters.coInvestor !== '') {
    chips.push({
      key: 'coInvestor',
      label: CO_INVESTOR_LABELS[filters.coInvestor],
      remove: (f) => ({ ...f, coInvestor: '' }),
    });
  }
  for (const stage of stageFilterChipStages({ mode: filters.stageMode, stages: filters.stages })) {
    chips.push({
      key: `stage:${stage ?? 'any'}`,
      label: stageFilterLabel(filters.stageMode, stage),
      remove: (f) => {
        const next = removeStageChip({ mode: f.stageMode, stages: f.stages }, stage);
        return { ...f, stageMode: next.mode, stages: [...next.stages] };
      },
    });
  }
  for (const type of filters.types) {
    chips.push({
      key: `type:${type}`,
      label: investorTypeLabel(type),
      remove: (f) => ({ ...f, types: without(f.types, type) }),
    });
  }
  for (const sector of filters.sectors) {
    chips.push({
      key: `sector:${sector}`,
      label: sector,
      remove: (f) => ({ ...f, sectors: without(f.sectors, sector) }),
    });
  }
  for (const region of filters.regions) {
    chips.push({
      key: `region:${region}`,
      label: region === EUROPE_FILTER ? 'Europe (any)' : region,
      remove: (f) => ({ ...f, regions: without(f.regions, region) }),
    });
  }
  if (filters.tier !== '') {
    chips.push({
      key: 'tier',
      label: `Tier ${filters.tier}`,
      remove: (f) => ({ ...f, tier: '' }),
    });
  }
  for (const code of filters.hq) {
    chips.push({
      key: `hq:${code}`,
      label: `HQ ${countryName(code)}`,
      remove: (f) => ({ ...f, hq: without(f.hq, code) }),
    });
  }
  if (filters.activity !== '') {
    chips.push({
      key: 'activity',
      label: ACTIVITY_LABELS[filters.activity],
      remove: (f) => ({ ...f, activity: '' }),
    });
  }
  return chips;
}
